import React, { useState, useEffect } from "react";
import {
    Container,
    Card,
    Form,
    Button,
    Table,
    Row,
    Col,
    Badge,
    Spinner,
    Modal,
} from "react-bootstrap";
import { t } from "i18next";
import DataManager from "../../../General/DataManager";
import UserClass from "../../../General/LocalStorage/UserClass";
import Functions from "../../../General/Functions";
import { useNavigate, useSearchParams } from "react-router-dom";

const RouteBoardingManagement = () => {

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = useState({
        company: "",
        port: "",
        departureDate: "",
        departureTime: "",
    });

    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);

    const [selectedRoute, setSelectedRoute] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const [ports, setPorts] = useState([]);
    const [portsError, setPortsError] = useState(null);
    const [isLoadingPorts, setIsLoadingPorts] = useState(false);
    const [companies, setCompanies] = useState([]);

    const [showRecipientsModal, setShowRecipientsModal] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [manualEmails, setManualEmails] = useState([]);
    const [emailInput, setEmailInput] = useState("");
    const [closingBoarding, setClosingBoarding] = useState(false);

    const isSearchDisabled =
        !filters.company || !filters.port || !filters.departureDate;


    useEffect(() => {
        getCompanies();

        const restoredFilters = {
            company: (searchParams.get("company") || "")?.toUpperCase(),
            port: (searchParams.get("port") || "").toUpperCase(),
            departureDate: searchParams.get("departureDate") || "",
            departureTime: searchParams.get("departureTime") || ""
        };

        if (restoredFilters.company &&
            restoredFilters.port &&
            restoredFilters.departureDate) {

            setFilters(restoredFilters);

            getPorts(restoredFilters.company);

            searchRoutes(restoredFilters);
        }

    }, []);

    const handleChange = (e) => {

        const { name, value } = e.target;

        if (name === "company") {
            setPorts([]);
            getPorts(value);

        }
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const searchRoutes = async (currentFilters) => {

        setLoading(true);
        setSearched(true);

        try {

            const instance = UserClass.getInstance();
            const token = instance.getTokenUser();

            const { data, msgError } = await DataManager.getRoutes(
                token,
                currentFilters.departureDate,
                currentFilters.port,
                currentFilters.departureTime || null,
                currentFilters.company || null
            );

            if (msgError) {
                Functions.alertMsg(msgError);
                setRoutes([]);
            } else {
                setRoutes(data || []);
            }

        } finally {
            setLoading(false);
        }

    };

    const handleSearch = async (e) => {
        e.preventDefault();

        if (!filters.company || !filters.port || !filters.departureDate) {
            Functions.alertMsg(t("please_fill_required_fields"));
            return;
        }

        setSearchParams({
            company: filters.company,
            port: filters.port,
            departureDate: filters.departureDate,
            ...(filters.departureTime && {
                departureTime: filters.departureTime
            })
        });

        searchRoutes(filters);
    };

    const confirmToggle = (route) => {
        setSelectedRoute(route);
        setShowModal(true);
    };

    const toggleBoarding = () => {
        if (selectedRoute.status === "Open") {
            // Closing boarding → show recipients modal
            setShowModal(false);
            loadUsers();
            setShowRecipientsModal(true);
            return;
        }

        // Opening boarding → no recipients, call API directly
        manageBoardingStatus(false); // false = opening
        setShowModal(false);
    };

    const getCompanies = () => {
        let instance = UserClass.getInstance();
        let token = instance.getTokenUser();

        DataManager.verifyLogin
            (token).then((result) => {
                if (typeof (result) !== 'undefined' && result !== null) {
                    if (typeof (result['verifylogin']) !== 'undefined' && result['verifylogin'] !== null) {

                        setCompanies(result['verifylogin']['companies'])

                        if (result['verifylogin']['companies']?.length === 1) {
                            setPorts([]);
                            getPorts(result['verifylogin']['companies'][0]);

                            setFilters(prev => ({
                                ...prev,
                                company:
                                    prev.company || result['verifylogin']['companies'][0]
                            }));
                        }
                    } else {
                        if (typeof (result['msgError']) !== 'undefined' && result['msgError'] !== null && result['msgError'] !== "") {
                            Functions.alertMsg(result['msgError']);
                        } else {
                            Functions.alertMsg(t("msgErrorGeneral"));
                        }
                    }
                } else {
                    Functions.alertMsg(t("msgErrorGeneral"));
                }
            });
    }

    const loadUsers = async () => {
        try {
            const instance = UserClass.getInstance();
            const token = instance.getTokenUser();

            const { data, msgError } = await DataManager.getManageBoardingUserList(token, filters.company);


            if (msgError) {
                Functions.alertMsg(msgError);
                return;
            }

            setUsers(data || []);
        } catch {
            Functions.alertMsg(t("msgErrorGeneral"));
        }
    };

    const toggleUserSelection = (user) => {
        setSelectedUsers((prev) =>
            prev.includes(user.email)
                ? prev.filter((u) => u !== user.email)
                : [...prev, user.email]
        );
    };

    const addEmail = () => {
        const email = emailInput.trim();

        if (!email) return;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(email)) {
            Functions.alertMsg(t("invalid_email"));
            return;
        }

        if (!manualEmails.includes(email)) {
            setManualEmails([...manualEmails, email]);
        }

        setEmailInput("");
    };

    const removeEmail = (email) => {
        setManualEmails(manualEmails.filter((e) => e !== email));
    };

    const manageBoardingStatus = async (close) => {
        if (!selectedRoute) return;

        const finalPayload = {
            routeId: selectedRoute.id,
            departurePort: filters.port,
            close, // true = close, false = open
            notificationRecipients: close ? [...selectedUsers, ...manualEmails].map(email => ({
                email,
                channels: ["EMAIL"],
            })) : [],
        };

        try {
            setClosingBoarding(true);

            const instance = UserClass.getInstance();
            const token = instance.getTokenUser();

            const { msgError } = await DataManager.manageBoarding(token, finalPayload);

            if (msgError) {
                Functions.alertMsg(msgError);
                return;
            }

            // Update route in table
            setRoutes(prev =>
                prev.map(route =>
                    route.id === selectedRoute.id
                        ? { ...route, boardingOpen: !close, status: close ? "Closed" : "Open" }
                        : route
                )
            );

            // Clear modal state
            setSelectedUsers([]);
            setManualEmails([]);
            setEmailInput("");
            setSelectedRoute(null);
            setShowRecipientsModal(false);

            Functions.alertMsgSuccess(
                close ? t("boarding_closed_successfully") : t("boarding_opened_successfully"),
                "success"
            );
        } catch (error) {
            Functions.alertMsg(t("msgErrorGeneral"));
        } finally {
            setClosingBoarding(false);
        }
    };

    const getPorts = async (company) => {
        setIsLoadingPorts(true);
        setPortsError(null);

        try {
            const instance = UserClass.getInstance();
            const token = instance.getTokenUser();

            const { ports, msgError } = await DataManager.getPorts(company, token);


            if (msgError) {
                throw new Error(msgError);
            }

            setPorts(ports["results"] ?? []);
        } catch (error) {
            setPorts([]);
            setPortsError(error.message || t("msgErrorGeneral"));
        } finally {
            setIsLoadingPorts(false);
        }
    };

    const totalRoutes = routes.length;
    const openRoutes = routes.filter((r) => r.status === "Open").length;
    const closedRoutes = routes.filter((r) => r.status !== "Open").length;
    return (
        <Container fluid className="mt-4">
            <h4 className="fw-bold mb-4">{t("route_boarding_management")}</h4>

            {searched && (
                <Row className="mb-4">
                    <Col md={4}>
                        <Card className="shadow-sm border-0 text-center">
                            <Card.Body>
                                <h6 className="text-muted">{t("total_routes")}</h6>
                                <h3>{totalRoutes}</h3>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="shadow-sm border-0 text-center">
                            <Card.Body>
                                <h6 className="text-success">{t("boarding_open")}</h6>
                                <h3>{openRoutes}</h3>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card className="shadow-sm border-0 text-center">
                            <Card.Body>
                                <h6 className="text-danger">{t("boarding_closed")}</h6>
                                <h3>{closedRoutes}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            <Card className="shadow-sm mb-4 border-0">
                <Card.Body>
                    <Form onSubmit={handleSearch}>
                        <Row className="align-items-end">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>{t("company")}</Form.Label>
                                    <Form.Select
                                        name="company"
                                        value={filters.company}
                                        onChange={handleChange}
                                        required
                                    >
                                        {companies.length === 1 ? (
                                            <option value={companies[0]}>
                                                {companies[0]}
                                            </option>
                                        ) : (
                                            <>
                                                <option value="">{t("select_company")}</option>
                                                {companies.map((company, index) => (
                                                    <option key={index} value={company}>
                                                        {company}
                                                    </option>
                                                ))}
                                            </>
                                        )}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>{t("port")}</Form.Label>
                                    <Form.Select
                                        name="port"
                                        value={filters.port}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">{t("select_port")}</option>
                                        {ports.map((port, index) => (
                                            <option key={index} value={port}>
                                                {port}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>{t("departure_date")}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="departureDate"
                                        value={filters.departureDate}
                                        onChange={handleChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>{t("departure_time")}</Form.Label>
                                    <Form.Control
                                        type="time"
                                        name="departureTime"
                                        value={filters.departureTime}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={2}>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="w-100"
                                    disabled={loading || isSearchDisabled}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            {t("searching")}
                                        </>
                                    ) : (
                                        t("search_routes")
                                    )}
                                </Button>

                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {searched && (
                <Card className="shadow-sm border-0">
                    <Card.Body>
                        {routes.length === 0 && !loading ? (
                            <p className="text-muted text-center">
                                {t("no_routes_found")}
                            </p>
                        ) : (
                            <Table hover responsive className="align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th className="text-nowrap">{t("route_id")}</th>
                                        <th className="text-nowrap">{t("route_description")}</th>
                                        <th className="text-nowrap">{t("departure_time")}</th>
                                        <th className="text-nowrap">{t("status")}</th>
                                        <th className="text-nowrap">{t("managed_time")}</th>
                                        <th className="text-nowrap">{t("managed_user")}</th>
                                        <th className="text-center text-nowrap">{t("action")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {routes.map((route) => (
                                        <tr key={route.id}>
                                            <td className="fw-semibold">{route.id}</td>
                                            <td>{route.description}</td>
                                            <td>{new Date(route.departureTime).toLocaleString()}</td>
                                            <td>
                                                {route.status === "Open" ? (
                                                    <Badge bg="success">
                                                        {t("open")}
                                                    </Badge>
                                                ) : (
                                                    <Badge bg="danger">
                                                        {t("closed")}
                                                    </Badge>
                                                )}
                                            </td>
                                            <td>
                                                {route.managedTime ? new Date(route.managedTime).toLocaleString() : "-"}
                                            </td>

                                            <td>
                                                {route.managedUser ? route.managedUser : "-"}
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-2">

                                                    <Button
                                                        size="sm"
                                                        variant={
                                                            route.status === "Open"
                                                                ? "outline-danger"
                                                                : "outline-success"
                                                        }
                                                        onClick={() => confirmToggle(route)}
                                                    >
                                                        {route.status === "Open"
                                                            ? t("close_boarding")
                                                            : t("open_boarding")}
                                                    </Button>


                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => {
                                                            const params = new URLSearchParams({
                                                                description: route.description,
                                                                port: filters.port,
                                                                departureTime: route.departureTime
                                                            });
                                                            navigate(`/pages/boardingTickets/${route.id}?${params}`)
                                                        }
                                                        }
                                                    >
                                                        {t("tickets")}
                                                    </Button>

                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t("confirm_action")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedRoute &&
                        t("confirm_boarding_action", {
                            action: selectedRoute?.status === "Open"
                                ? t("close_lowercase")
                                : t("open_lowercase"),
                            code: (
                                selectedRoute?.description
                                    ? selectedRoute.description
                                        .toString()
                                        .toLowerCase()
                                        .split(' ')
                                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                        .join(' ')
                                    : ""
                            ) + " " + new Date(selectedRoute?.departureTime).toLocaleString(),
                        })}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        {t("cancel")}
                    </Button>
                    <Button
                        variant={
                            selectedRoute?.status === "Open" ? "danger" : "success"
                        }
                        onClick={toggleBoarding}
                    >
                        {t("confirm")}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showRecipientsModal}
                onHide={() => setShowRecipientsModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("select_report_recipients")}</Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
                    <h6 className="mb-3">{t("system_users")}</h6>

                    {users.length === 0 ? (
                        <div className="text-center text-muted py-4">
                            {t("no_users_available")}
                        </div>
                    ) : (<Table hover responsive>
                        <thead>
                            <tr>
                                <th></th>
                                <th>{t("first_name")}</th>
                                <th>{t("last_name")}</th>
                                <th>{t("email")}</th>
                            </tr>
                        </thead>

                        <tbody>
                            {users.map((user, index) => (
                                <tr key={index}>
                                    <td className="text-center align-middle">
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedUsers.includes(user.email)}
                                            onChange={() => toggleUserSelection(user)}
                                        />
                                    </td>
                                    <td>{user.firstName}</td>
                                    <td>{user.lastName}</td>
                                    <td>{user.email}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>)}


                    < hr />

                    <h6>{t("additional_emails")}</h6>

                    <div className="d-flex mb-3">
                        <Form.Control
                            type="email"
                            placeholder={t("enter_email")}
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addEmail()}
                        />

                        <Button
                            className="ms-2"
                            variant="outline-primary"
                            onClick={addEmail}
                        >
                            {t("add")}
                        </Button>
                    </div>

                    <div>
                        {manualEmails.map((email, index) => (
                            <Badge
                                bg="secondary"
                                className="me-2 mb-2 p-2"
                                key={index}
                                style={{ cursor: "pointer" }}
                                onClick={() => removeEmail(email)}
                            >
                                {email} ✕
                            </Badge>
                        ))}
                    </div>

                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowRecipientsModal(false)}
                    >
                        {t("cancel")}
                    </Button>

                    <Button
                        variant="danger"
                        onClick={() => manageBoardingStatus(true)}
                        disabled={closingBoarding}
                    >
                        {closingBoarding ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {t("closing")}
                            </>
                        ) : (
                            t("close_boarding")
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default RouteBoardingManagement;